import * as Y from "yjs";
import { type ProsemirrorBinding, ySyncPluginKey } from "y-prosemirror";
import type { EditorView } from "@tiptap/pm/view";
import type { User } from "$stores/user";
import { yDocToProsemirrorJSON } from "y-prosemirror";
import { isEqual } from "lodash-es";
import { writable } from "svelte/store";

const snapshotOrigin = "snapshot-origin";

/*
 * Versioning
 *
 * This module is responsible for managing the version history of the document.
 *
 * doc: Y.Doc
 * versions: Y.Array<any> - a list of versions (snapshots + metadata) of the document
 * users: Y.Map<any> - a mapping of clients that have made changes to the document
 * default: Y.Map<any> - the tiptap/prosemirror content of the document
 *
 * The version history is a list of versions of the document stored within a "versions" array inside the document.
 *
 * Each version is a snapshot of the document at a point in time, and is stored as an encoded Y.Snapshot object.
 *
 * We support creating, viewing and reverting a document to a previous version. Reverting is done "git revert" style,
 * where the versions history is kept and the document content reverted. This means that all clients will see the
 * reversion, and that the history of the document is maintained - ensuring clients don't have conflicting histories or
 * reset the document to previous states.
 *
 */

interface Version {
  name: string;
  createdAt: number;
  snapshot: Uint8Array;
  clientID: number;
  versionID: number;
  revertedID: number | null;
  isReversion: boolean;
  comment: string;
  author: {
    uuid: string;
    handle: string;
    profileImage: string;
  };
}

/**
 * Returns a list of versions of the document.
 *
 * @param doc
 * @returns a list of versions of the document
 */

export const getVersionsList = (doc: Y.Doc) => {
  return (getVersions(doc).map((v: any) => v) || []) as Version[];
};

/**
 * Returns the versions yjs array of the document.
 *
 * This is not the same as a normal list, but you can listen to it for changes from other clients - magic 🪄.
 * @param doc
 * @returns the version history of the document
 */

export const getVersions = (doc: Y.Doc) => {
  return doc.getArray<Version>("versions") as Y.Array<Version>;
};

export const hasChanges = (doc: Y.Doc, versions: Y.Array<Version>) => {
  const snapshot = Y.snapshot(doc);
  const prevVersion = versions.length ? versions.get(0) : null;
  const prevSnapshot = prevVersion ? Y.decodeSnapshot(prevVersion.snapshot) : Y.emptySnapshot;

  if (prevVersion) {
    // account for the action of adding a version to ydoc
    prevSnapshot.sv.set(prevVersion.clientID, prevSnapshot.sv.get(prevVersion.clientID)! + 1);
  }

  // cheaply compare snapshots
  if (Y.equalSnapshots(prevSnapshot, snapshot)) {
    return false;
  }
  // drafts could have been previously reverted and so the draft can still have "changes"

  // expensive comparison of the full document to check any material changes
  const draft = Y.createDocFromSnapshot(doc, prevSnapshot, new Y.Doc());
  return !isEqual(yDocToProsemirrorJSON(doc, "default"), yDocToProsemirrorJSON(draft, "default"));
};

/**
 * Takes a snapshot of the current doc and adds it to the version history of the document.
 *
 * @param doc
 * @param versions
 * @param comment
 * @returns the version that was added
 */

export const addVersion = (
  doc: Y.Doc,
  versions: Y.Array<Version>,
  comment: string,
  author: { handle: string; profileImage: string; uuid: string }
) => {
  const prevVersion = versions.length ? versions.get(0) : null;
  const prevSnapshot = prevVersion ? Y.decodeSnapshot(prevVersion.snapshot) : Y.emptySnapshot;
  const snapshot = Y.snapshot(doc);

  if (prevVersion) {
    // account for the action of adding a version to ydoc
    prevSnapshot.sv.set(prevVersion.clientID, prevSnapshot.sv.get(prevVersion.clientID)! + 1);
  }

  if (!Y.equalSnapshots(prevSnapshot, snapshot)) {
    const version: Version = {
      name: `Version ${versions.length}`,
      createdAt: new Date().getTime(),
      snapshot: Y.encodeSnapshot(snapshot),
      clientID: doc.clientID,
      versionID: versions.length,
      revertedID: null,
      isReversion: false,
      comment,
      author,
    };
    versions.insert(0, [version]);
    return version;
  }
  return null;
};

export const removeVersion = (versions: Y.Array<Version>) => {
  versions.delete(0);
};

export const renderVersionDiff = (view: EditorView, versions: Y.Array<Version>, index: number) => {
  const snapshot = index < 0 ? null : Y.decodeSnapshot(versions.get(index).snapshot);
  const prevSnapshot =
    index + 1 < versions.length
      ? Y.decodeSnapshot(versions.get(index + 1).snapshot)
      : Y.emptySnapshot;
  const binding: ProsemirrorBinding | null = ySyncPluginKey.getState(view.state).binding;
  // override type, it accepts nulls to display current diff
  binding?.renderSnapshot(snapshot as Y.Snapshot, prevSnapshot);
};

export const renderVersion = (view: EditorView, versions: Y.Array<Version>, index: number) => {
  const snapshot =
    index < 0 ? null : (Y.decodeSnapshot(versions.get(index).snapshot) as Y.Snapshot);
  const binding: ProsemirrorBinding | null = ySyncPluginKey.getState(view.state).binding;
  // override type, it accepts nulls to display current diff
  binding?.renderSnapshot(snapshot as Y.Snapshot, snapshot as Y.Snapshot);
};

export const unrenderVersion = (view: EditorView) => {
  const binding: ProsemirrorBinding | null = ySyncPluginKey.getState(view.state).binding;
  binding?.unrenderSnapshot();
};

/**
 * Reverts the document to the state it was in at the time of the snapshot (git revert style).
 *
 * All clients synced to this doc will see this update.
 *
 * WARN: This is a destructive operation, and will remove all changes made since the snapshot.
 *
 * @param doc
 * @param snapshotUpdate
 */
export function revertUpdate(doc: Y.Doc, version: Version, user: User) {
  resetToSnapshot(doc, Y.decodeSnapshot(version.snapshot), user);

  // add a snapshot to the version history to record the reversion
  const versions = getVersions(doc);
  const snapshot = Y.snapshot(doc);
  const newVersion = {
    name: `Version ${versions.length}`,
    createdAt: new Date().getTime(),
    snapshot: Y.encodeSnapshot(snapshot),
    clientID: doc.clientID,
    versionID: versions.length,
    revertedID: version.versionID,
    isReversion: true,
    comment: "Reverted",
    author: {
      uuid: user.uuid,
      handle: user.handle!,
      profileImage: user.profileImage!,
    },
  };
  versions.insert(0, [newVersion]);
  return version;
}

export function clearDraft(doc: Y.Doc) {
  const latestVersion = getVersions(doc).get(0);
  const prevSnapshot = Y.decodeSnapshot(latestVersion.snapshot);
  const currentSnapshot = Y.snapshot(doc);
  if (latestVersion) {
    // account for the action of adding a version to ydoc
    prevSnapshot.sv.set(latestVersion.clientID, prevSnapshot.sv.get(latestVersion.clientID)! + 1);
  }

  if (!Y.equalSnapshots(prevSnapshot, currentSnapshot)) {
    resetToSnapshot(doc, Y.decodeSnapshot(latestVersion.snapshot));
    return true;
  }
  return false;
}

function resetToSnapshot(doc: Y.Doc, snapshot: Y.Snapshot, user?: User) {
  // We want to do the equivalent of a git revert rather than a rebase (losing the history), that way all clients
  // move forward with the doc rather than having conflicting histories which risk clobbering one another.

  // To acheive this we apply the inverse of the changes that have been made since the snapshot.
  // First, calculate the changes that have been made since the snapshot as an update and apply that to the
  // snapshot doc, before then using the undo manager to revert those changes.

  // One thing to note is this is only applied to the document "content" inside the ydoc, and not the clients (users) or versions (snapshots), that way we
  // keep a history of the versions and don't lose records of users that have made edits in between the reversion.
  const snapshotDoc = Y.createDocFromSnapshot(doc, snapshot, new Y.Doc({ gc: false }));
  snapshotDoc.gc = false;

  if (user) {
    // associate changes made during the revert action with the reverting user
    const pud = new Y.PermanentUserData(doc);
    pud.setUserMapping(doc, snapshotDoc.clientID, user.handle!);
  }

  // create an update that represents the changes that have been made since the snapshot
  const snapshotStateVector = Y.encodeStateVector(snapshotDoc);
  const currentStateVector = Y.encodeStateVector(doc);
  const changesSinceSnapshotUpdate = Y.encodeStateAsUpdate(doc, snapshotStateVector);

  // attach an undo manager to the snapshot doc that will be used to revert the changes
  const undoManager = new Y.UndoManager(
    snapshotDoc.getMap("default"), // only track changes to the content of the document
    {
      trackedOrigins: new Set([snapshotOrigin]),
      // specify trackedOrigins to specify which changes should be tracked by UndoManager
    }
  );
  // apply the changes to the snapshot doc
  Y.applyUpdate(snapshotDoc, changesSinceSnapshotUpdate, snapshotOrigin);

  // revert those changes
  undoManager.undo();
  // calculate and apply the changes back to the original doc
  const revertChangesSinceSnapshotUpdate = Y.encodeStateAsUpdate(snapshotDoc, currentStateVector);
  Y.applyUpdate(doc, revertChangesSinceSnapshotUpdate, snapshotOrigin);
}

export const versionHistoryModalOpen = writable(false);
